import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ApolloClient } from "@apollo/client";
import { createTranslate, type WorkspaceTranslate } from "./i18n";

export interface IMarketingWorkspaceConfig {
  getClient: () => ApolloClient;
  mode: "multi" | "single";
  plansEnabled: boolean;
  locale: string;
  /** Who is signed in, so a screen can tell "me" from another member. */
  currentUserEmail?: string;
}

interface IMarketingWorkspaceConfigValue extends IMarketingWorkspaceConfig {
  t: WorkspaceTranslate;
}

const MarketingWorkspaceConfigContext = createContext<IMarketingWorkspaceConfigValue | null>(null);

export function MarketingWorkspaceConfigProvider({
  config,
  children,
}: {
  config: IMarketingWorkspaceConfig;
  children: ReactNode;
}) {
  const value = useMemo<IMarketingWorkspaceConfigValue>(
    () => ({ ...config, t: createTranslate(config.locale) }),
    [config]
  );

  return (
    <MarketingWorkspaceConfigContext.Provider value={value}>
      {children}
    </MarketingWorkspaceConfigContext.Provider>
  );
}

export function useMarketingWorkspaceConfig(): IMarketingWorkspaceConfigValue {
  const config = useContext(MarketingWorkspaceConfigContext);

  if (!config) {
    throw new Error(
      "Marketing workspace components must be rendered inside <MarketingWorkspaceProvider>"
    );
  }

  return config;
}

export function useWorkspaceTranslate(): WorkspaceTranslate {
  return useMarketingWorkspaceConfig().t;
}
